import React, { useState } from 'react';
import './Timeline.scss';

interface TimelineStep {
  id: number;
  titulo: string;
  descricao: string;
  dataInicio: string;
  dataFim: string;
  status: 'concluido' | 'em_andamento' | 'pendente';
  percentual?: number;
  badge?: string;
  _id?: string;
}

interface TimelineProps {
  steps: TimelineStep[];
}

export const Timeline: React.FC<TimelineProps> = ({ steps }) => {
  const [hoveredStepId, setHoveredStepId] = useState<number | null>(null);

  const getProgressText = (step: TimelineStep): string => {
    const stepId = step._id || '';
    const percentual = step.percentual || 0;

    switch (stepId) {
      case 'inscriptions':
        return `${percentual}% das iniciativas inscritas`;
      case 'step_1':
        return `${percentual}% das iniciativas triadas`;
      case 'step_2':
        return `${percentual}% das iniciativas avaliadas`;
      case 'apuration_1':
      case 'apuration_2':
        return `${percentual}% da apuração concluída`;
      case 'step_3':
        return `${percentual}% dos votos computados`;
      case 'step_4':
        return `Cerimônia de premiação`;
      default:
        return `${percentual}% concluído`;
    }
  };

  return (
    <div className="timeline">
      {/* Cards dos passos - posicionados acima da timeline */}
      <div className="timeline__cards">
        {steps.map((step, index) => {
          // Calcular posição do card baseada na posição do ícone
          const totalSteps = steps.length;
          const padding = 2; // rem
          
          // Posição horizontal calculada dinamicamente
          const leftPosition = index === 0 
            ? `calc(${padding}rem + 24px)` // primeiro ícone
            : index === totalSteps - 1
            ? `calc(100% - ${padding}rem - 24px)` // último ícone
            : `calc(${padding}rem + 24px + (100% - ${padding * 2}rem - 48px) * ${index} / ${totalSteps - 1})`;

          return (
            <div
              key={step.id}
              className={`timeline__card timeline__card--${step.status} ${
                step.status === 'em_andamento' || hoveredStepId === step.id ? 'timeline__card--visible' : ''
              }`}
              style={{ left: leftPosition }}
            >
              {step.badge && (
                <div className="timeline__card-badge">{step.badge}</div>
              )}
              
              {/* Ícone Mobile */}
              <div className={`timeline__mobile-icon timeline__mobile-icon--${step.status}`}>
                {step.status === 'concluido' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                  </svg>
                )}
                {step.status === 'em_andamento' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="timeline__spinner">
                    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" strokeDasharray="12 8" />
                    <circle cx="12" cy="12" r="1" fill="currentColor" />
                  </svg>
                )}
                {step.status === 'pendente' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" fill="currentColor"/>
                  </svg>
                )}
              </div>

              <h3 className="timeline__card-title">{step.titulo}</h3>
              <p className="timeline__card-description">{step.descricao}</p>
              <p className="timeline__card-dates">
                De: <strong>{step.dataInicio}</strong> Até: <strong>{step.dataFim}</strong>
              </p>

              {step.status === 'em_andamento' && step.percentual !== undefined && (
                <div className="timeline__card-progress">
                  <div className="timeline__progress-bar">
                    <div 
                      className="timeline__progress-fill"
                      style={{ width: `${step.percentual}%` }}
                    />
                  </div>
                  <p className="timeline__progress-text">
                    {getProgressText(step)}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Linha do tempo com ícones */}
      <div className="timeline__track">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Linha conectora */}
            {index > 0 && (
              <div 
                className={`timeline__connector timeline__connector--${
                  steps[index - 1].status === 'concluido' ? 'completed' : 'locked'
                }`}
              />
            )}
            
            {/* Ícone do passo */}
            <div 
              className={`timeline__icon timeline__icon--${step.status}`}
              onMouseEnter={() => setHoveredStepId(step.id)}
              onMouseLeave={() => setHoveredStepId(null)}
            >
              {step.status === 'concluido' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" 
                    fill="currentColor"
                  />
                </svg>
              )}
              {step.status === 'em_andamento' && (
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none"
                  className="timeline__spinner"
                >
                  <circle 
                    cx="12" 
                    cy="12" 
                    r="8" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeDasharray="12 8"
                  />
                </svg>
              )}
              {step.status === 'pendente' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" 
                    fill="currentColor"
                  />
                </svg>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
